import type { ReactNode } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TreeItem } from '@mui/x-tree-view';

import type { Id } from '@e-socna/data-layer';

import { EllipsisTooltip } from '../../../../EllipsisTooltip';
import { MessageList } from '../../../../MessageList';
import {
  fileCategoryAccessOf,
  isFileCategoryLocked,
  lockReasonsOf,
} from '../../../utils/file-category-access.util';
import type { FileCategoryAccesses } from '../../../types/file-category-access.type';

import type { FileCategoryTreeNode } from './build-file-category-tree.util';

export interface FileCategory {
  readonly _id: Id;
  readonly label: string;
  readonly parentCategoryId?: Id | null;
}

const renderFileCategoryLabel = (
  category: FileCategory,
  fileCount: number | undefined,
): ReactNode => (
  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
    <EllipsisTooltip title={category.label}>
      <Typography noWrap sx={{ flexGrow: 1, minWidth: 0 }} variant="body2">
        {category.label}
      </Typography>
    </EllipsisTooltip>
    {fileCount ? (
      <Avatar
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          flexShrink: 0,
          fontSize: '0.7rem',
          height: 20,
          width: fileCount > 99 ? 'auto' : 20,
          px: fileCount > 99 ? 0.75 : 0,
        }}
      >
        {fileCount > 99 ? '99+' : fileCount}
      </Avatar>
    ) : null}
  </Box>
);

// A category where nothing is possible is greyed out, the tooltip giving the reasons.
export const renderFileCategoryTreeItems = (
  nodes: readonly FileCategoryTreeNode<FileCategory>[],
  fileCountsByCategoryId: ReadonlyMap<Id, number> | undefined,
  accesses?: FileCategoryAccesses,
): ReactNode =>
  nodes.map(({ category, children }) => {
    const access = fileCategoryAccessOf(accesses, category._id);
    const isLocked = isFileCategoryLocked(access);
    const label = renderFileCategoryLabel(
      category,
      fileCountsByCategoryId?.get(category._id),
    );

    return (
      <TreeItem
        disabled={isLocked}
        itemId={category._id}
        key={category._id}
        label={
          isLocked ? (
            <Tooltip
              placement="right"
              title={<MessageList messages={lockReasonsOf(access)} />}
            >
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          )
        }
      >
        {children.length > 0
          ? renderFileCategoryTreeItems(
              children,
              fileCountsByCategoryId,
              accesses,
            )
          : null}
      </TreeItem>
    );
  });
